import { BookOpen, CalendarDays } from 'lucide-react';

import { NextSessionCard } from './home/NextSessionCard';
import { ContinueCard } from './home/ContinueCard';
import { TodayTimeline } from './home/TodayTimeline';
import { BatchesPanel } from './home/BatchesPanel';
import { ProgressPanel } from './home/ProgressPanel';
import { AnnouncementsCard } from './home/AnnouncementsCard';
import { PlaceholderCard } from './home/PlaceholderCard';

import { EmptyState } from '@/components/EmptyState';
import { useHome } from '@/lib/hooks/useHome';
import { useCurrentBatch, ALL_BATCHES } from '@/lib/hooks/useCurrentBatch';


export function StudentHome() {
  const { batchId, batches, isLoading: batchLoading, isAll } = useCurrentBatch();
  const homeBatchId = isAll ? ALL_BATCHES : (batchId ?? undefined);
  const { data, isLoading, isError, refetch } = useHome(homeBatchId);

  if (!batchLoading && (!batches || batches.length === 0)) {
    return (
      <div className="mx-auto max-w-[1200px] p-4 sm:p-6">
        <EmptyState icon={BookOpen} title="No courses yet"
          description="You haven't been enrolled in any courses. Contact your trainer or admin to get started." />
      </div>
    );
  }

  if (isLoading || batchLoading) return <HomeSkeleton />;

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-[1200px] p-4 sm:p-6">
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-muted-foreground">Couldn&apos;t load your dashboard.</p>
          <button onClick={() => refetch()} className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Retry</button>
        </div>
      </div>
    );
  }

  const sortedBatches = [...(data.batches ?? [])].sort((a, b) =>
    a.id === batchId ? -1 : b.id === batchId ? 1 : 0
  );

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 p-4 sm:px-8 sm:py-7">
      {/* Hero: Next session */}
      {data.next_session ? (
        <NextSessionCard session={data.next_session} batchId={batchId ?? undefined} />
      ) : (
        <PlaceholderCard icon={CalendarDays} title="No upcoming class"
          description="You're all caught up on live sessions — check back later." />
      )}

      {/* Two columns per the design: a fixed timeline rail on the left, and a
          stacked batches → progress column on the right. items-start keeps
          each column at its own content height. */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[420px_1fr] lg:gap-[11px]">
        <div className="flex flex-col gap-6">
          {/* today_events already has the hero's session filtered out server-side
              (lms.dropHeroSession), so the same class never renders twice. */}
          <TodayTimeline
            todayEvents={data.today_events ?? []}
            upcomingEvents={data.upcoming_events ?? []}
            dueItems={data.due_items ?? []}
          />
          {/* data.continue is always scoped to data.batch.id server-side, even
              when "All Courses" is selected — use that, not the dropdown's
              selection, or Resume links to a topic with no batch segment. */}
          <ContinueCard item={data.continue} batchId={data.batch.id} />
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <BatchesPanel batches={sortedBatches} />
          <ProgressPanel
            coursePct={Math.round(data.stats.course_pct)}
            attendancePct={Math.round(data.readiness?.attendance_pct ?? 0)}
            streakDays={data.streak?.current ?? 0}
          />
          {(data.announcements ?? []).length > 0 && (
            <AnnouncementsCard announcements={data.announcements ?? []} />
          )}
        </div>
      </div>
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div className="mx-auto max-w-[1200px] p-4 sm:p-6 space-y-6">
      <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr_300px]">
        <div className="h-64 animate-pulse rounded-2xl bg-muted" />
        <div className="h-64 animate-pulse rounded-2xl bg-muted" />
        <div className="h-64 animate-pulse rounded-2xl bg-muted" />
      </div>
    </div>
  );
}
